import { CalendarTodayTwoTone, DeleteOutline, Edit } from '@mui/icons-material';
import { Button, IconButton } from '@mui/material';
import { DataGrid, GridCellParams, GridColDef } from '@mui/x-data-grid';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal, IUserRoles, Role, useGetUserRoles, useOrganisationRecords, Widget } from '../../../shared';
import { UserRolesModal } from '../user-roles-modal/user-roles-modal.component';
import { useDeleteUserRole } from '../../hooks/user-role.hook';

interface Props {
    userId: string;
}

export const UserRoles: FC<Props> = ({ userId }) => {
    const { t } = useTranslation();
    const [saveUserRolesModal, setSaveUserRolesModal] = useState<{ show: boolean; role?: IUserRoles }>({
        show: false,
        role: undefined,
    });

    const { data: userRoles, isPending } = useGetUserRoles(userId, {});
    const { mutateAsync: deleteUserRoles } = useDeleteUserRole();
    const organisations = useOrganisationRecords();

    const columns: GridColDef[] = [
        {
            field: 'context',
            headerName: t('organisation'),
            flex: 1,
            sortable: false,
            valueFormatter: (value: { key: string }) => organisations[value?.key]?.name || '',
        },
        {
            field: 'roles',
            headerName: t('roles'),
            flex: 1,
            sortable: false,
            valueFormatter: (value: Role[]) => value.map(({ key }) => key).join(', '),
        },
        {
            field: 'actions',
            headerName: t('actions'),
            width: 100,
            align: 'right',
            sortable: false,
            renderCell: ({ row, id }: GridCellParams<IUserRoles>) => (
                <>
                    <IconButton
                        onClick={() => setSaveUserRolesModal({ show: true, role: row })}
                        data-testid={`edit-${row.id}`}
                    >
                        <Edit />
                    </IconButton>
                    <ConfirmModal
                        onConfirm={() =>
                            deleteUserRoles({
                                id: row.id,
                                messages: {
                                    onDeleteSuccessMessage: t('deleteUserRolesSuccess'),
                                    onDeleteErrorMessage: t('deleteUserRolesError'),
                                },
                            })
                        }
                        button={
                            <IconButton aria-label="delete" data-testid={`delete-${id}`}>
                                <DeleteOutline />
                            </IconButton>
                        }
                        title={t('roleDeleteWarningTitle')}
                        text={t('roleDeleteWarningText')}
                    />
                </>
            ),
        },
    ];

    return (
        <Widget
            icon={<CalendarTodayTwoTone color="primary" />}
            title={t('roles')}
            hasTable
            sx={{ minWidth: '100%', border: '1px solid rgb(224,224,224)', boxShadow: 'none' }}
            actions={
                <Button
                    variant="outlined"
                    color="primary"
                    data-testid="add-user-roles"
                    onClick={() => setSaveUserRolesModal({ show: true, role: undefined })}
                >
                    + {t('addUserRoles')}
                </Button>
            }
        >
            <DataGrid
                sortingOrder={['asc', 'desc']}
                disableColumnFilter
                disableColumnMenu
                rows={userRoles?.items || []}
                columns={columns}
                rowCount={userRoles?.meta?.totalItems || 0}
                loading={isPending}
                pageSizeOptions={[5, 10, 20]}
                initialState={{
                    pagination: {
                        paginationModel: {
                            pageSize: 5,
                        },
                    },
                }}
                localeText={{ noRowsLabel: t('noRows') }}
            />

            <UserRolesModal
                userId={userId}
                allUserRoles={userRoles?.items || []}
                open={saveUserRolesModal.show}
                handleClose={() => setSaveUserRolesModal({ show: false, role: undefined })}
                userRolesOrganisation={saveUserRolesModal.role}
            />
        </Widget>
    );
};
